/**
 * @module ClaimsFormular
 * @description Multi-Step Wizard für Kunden-Schadensmeldungen.
 *   4 Schritte: Grunddaten → Details → Dokumente → Bestätigung.
 *   Erreichbar über /claims/new (Portal oder direkt).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Check, Upload, X, ChevronLeft, ChevronRight, FileText, Image, ArrowLeft, CheckCircle } from 'lucide-react';
import CustomerAutocompleteSearch from '../../components/CustomerAutocompleteSearch';

const API = import.meta.env.VITE_API_URL || '';

async function api(url, opts = {}) {
  const res = await fetch(API + url, { credentials: 'include', ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  return res.json();
}

const STEPS = ['claims.form.step1', 'claims.form.step2', 'claims.form.step3', 'claims.form.step4'];

export default function ClaimsFormular({ publicToken, customerName: publicCustomerName }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const isPublic = !!publicToken;
  // #1221 Bug 1+7a: Intern (admin/manager) vs Portal-MA (kunde) anhand der aktuellen URL unterscheiden
  const isPortal = !isPublic && typeof window !== 'undefined' && window.location.pathname.startsWith('/portal');
  const isIntern = !isPublic && !isPortal;
  // #1239 — Klärungsanfrage-Wizard vereinfachen. Default = Klärungs-Modus mit 1-Step-Form.
  // `?mode=full` reaktiviert den 4-Step Schadens-Wizard für komplette Damage-Submits.
  const isClarificationMode = typeof window !== 'undefined'
    && !new URLSearchParams(window.location.search).get('mode')?.includes('full');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [config, setConfig] = useState({ carriers: [], template_url: null, max_file_size_mb: 10 });
  const [success, setSuccess] = useState(null); // { claim_number, claim_id }

  // Form data
  const [form, setForm] = useState({
    order_number: '',
    carrier_id: '',
    tracking_number: '',
    claim_type: '', // 'damage' | 'investigation' | 'content_missing'  (#1217 A1)
    description: '',
    last_known_status: '',
    recipient_info: '',
    // #1217 A2 + D1: Lager + Erfassungs-Metadaten
    lager: '',
    submission_channel: '',
    post_tracking_nr: '',
    carrier_ticket_nr: '',
    confirmed: false,
    // #1221 Bug 7a: Kunden-Auswahl Pflicht für Intern-User (kein kunde_id im JWT)
    customer_id: '',
  });

  // #1221 Bug 7a: Kunden-Liste für Intern-Modus laden
  const [customers, setCustomers] = useState([]);
  useEffect(() => {
    if (!isIntern) return;
    fetch(API + '/api/kunden', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomers(Array.isArray(d) ? d.filter(k => k.aktiv !== false) : []))
      .catch(() => setCustomers([]));
  }, [isIntern]);

  // Files
  const [damagePhotos, setDamagePhotos] = useState([]);
  const [packagingPhotos, setPackagingPhotos] = useState([]);
  const [goodsValueProof, setGoodsValueProof] = useState([]);
  const [salesInvoice, setSalesInvoice] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]); // #1216 D3

  useEffect(() => {
    const configUrl = isPublic
      ? `/api/claims-portal/public/config?token=${encodeURIComponent(publicToken)}`
      : '/api/claims-portal/config';
    const opts = isPublic ? {} : { credentials: 'include' };
    fetch(API + configUrl, opts).then(r => r.json()).then(setConfig).catch(() => {});
  }, [isPublic, publicToken]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  // ── Validation ──

  function validateStep(s) {
    const errs = {};
    if (s === 0) {
      // #1221 Bug 7a: Intern → Kunden-Auswahl Pflicht (kein JWT-kunde_id)
      if (isIntern && !form.customer_id) errs.customer_id = true;
      // #1217 A2: Lager Pflicht vor Carrier-Auswahl
      if (!form.lager) errs.lager = true;
      if (!form.order_number.trim()) errs.order_number = true;
      if (!form.carrier_id) errs.carrier_id = true;
      if (!form.tracking_number.trim()) errs.tracking_number = true;
      if (!form.claim_type) errs.claim_type = true;
    }
    if (s === 1) {
      if (form.claim_type === 'damage') {
        if (!form.description || form.description.length < 20) errs.description = true;
        if (damagePhotos.length < 1) errs.damagePhotos = true;
        // #1216 A3: Verpackungsfoto Pflicht bei Schaden
        if (packagingPhotos.length < 1) errs.packagingPhotos = true;
      }
      // #1217 A1: content_missing — Beschreibung + Verpackungsfoto Pflicht
      if (form.claim_type === 'content_missing') {
        if (!form.description || form.description.length < 20) errs.description = true;
        if (packagingPhotos.length < 1) errs.packagingPhotos = true;
      }
    }
    if (s === 2) {
      if (goodsValueProof.length < 1) errs.goodsValueProof = true;
      if (salesInvoice.length < 1) errs.salesInvoice = true;
    }
    if (s === 3) {
      if (!form.confirmed) errs.confirmed = true;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function goNext() {
    if (validateStep(step)) setStep(s => Math.min(s + 1, 3));
  }

  function goBack() {
    setStep(s => Math.max(s - 1, 0));
  }

  // ── File handling ──

  function handleFiles(e, setter, getter, maxFiles = 10, acceptImages = true) {
    const incoming = Array.from(e.target?.files || e.dataTransfer?.files || []);
    const maxSizeBytes = config.max_file_size_mb * 1024 * 1024;
    const allowed = acceptImages
      ? ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      : ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

    const valid = incoming.filter(f => allowed.includes(f.type) && f.size <= maxSizeBytes);
    const combined = [...getter, ...valid].slice(0, maxFiles);
    setter(combined);
  }

  function removeFile(setter, getter, index) {
    setter(getter.filter((_, i) => i !== index));
  }

  // ── Submit ──

  async function handleSubmit() {
    if (!isClarificationMode && !validateStep(3)) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      // #1239 — Klärungs-Modus: nur Minimal-Felder (tracking_number Pflicht; order_number + description optional).
      if (isClarificationMode) {
        if (form.order_number) fd.append('order_number', form.order_number);
        fd.append('tracking_number', form.tracking_number);
        if (form.description) fd.append('description', form.description);
      } else {
        fd.append('order_number', form.order_number);
        fd.append('carrier_id', form.carrier_id);
        fd.append('tracking_number', form.tracking_number);
        fd.append('claim_type', form.claim_type);
        if (form.description) fd.append('description', form.description);
        if (form.last_known_status) fd.append('last_known_status', form.last_known_status);
        if (form.recipient_info) fd.append('recipient_info', form.recipient_info);
        // #1217 A2 + D1: Lager + Erfassungs-Metadaten
        if (form.lager) fd.append('lager', form.lager);
        if (form.submission_channel) fd.append('submission_channel', form.submission_channel);
        if (form.post_tracking_nr) fd.append('post_tracking_nr', form.post_tracking_nr);
        if (form.carrier_ticket_nr) fd.append('carrier_ticket_nr', form.carrier_ticket_nr);

        damagePhotos.forEach(f => fd.append('damage_photos', f));
        packagingPhotos.forEach(f => fd.append('packaging_photos', f));
        goodsValueProof.forEach(f => fd.append('goods_value_proof', f));
        salesInvoice.forEach(f => fd.append('sales_invoice', f));
        otherDocuments.forEach(f => fd.append('other_documents', f)); // #1216 D3
      }

      // #1221 Bug 7a: Intern-Wizard sendet Kunden-ID mit (Backend übernimmt nur für Intern-User)
      if (isIntern && form.customer_id) fd.append('customer_id', String(form.customer_id));
      if (isPublic) fd.append('token', publicToken);

      const submitUrl = isPublic ? '/api/claims-portal/public/submit' : '/api/claims-portal/submit';
      const fetchOpts = { method: 'POST', body: fd };
      if (!isPublic) fetchOpts.credentials = 'include';
      const res = await fetch(API + submitUrl, fetchOpts);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      setSuccess({ claim_number: data.claim_number, claim_id: data.claim_id });
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  // #1239 — Minimal-Validation für Klärungs-Modus.
  function validateClarification() {
    const errs = {};
    if (isIntern && !form.customer_id) errs.customer_id = true;
    if (!form.tracking_number.trim()) errs.tracking_number = true;
    if (!form.description || form.description.trim().length < 10) errs.description = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleClarificationSubmit() {
    if (validateClarification()) handleSubmit();
  }

  // ── Success Page ──

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-page)' }}>
        <div className="w-full max-w-md text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 16, padding: 40 }}>
          <div className="flex justify-center mb-4">
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={32} style={{ color: '#10B981' }} />
            </div>
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('claims.form.success.title')}</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.success.text')}</p>
          <div style={{ background: '#EBF4FD', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }} className="dark:bg-primary-500/10">
            <div className="text-xs uppercase tracking-wider mb-1" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.success.claimNumber')}</div>
            <div className="text-2xl font-bold" style={{ color: '#338edc' }}>{success.claim_number}</div>
          </div>
          <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.success.info')}</p>
          {!isPublic && (
            <div className="flex flex-col gap-2">
              {isIntern && success.claim_id && (
                <button
                  onClick={() => navigate('/claims/' + success.claim_id)}
                  className="w-full py-3 px-6 rounded-xl text-sm font-semibold text-white"
                  style={{ background: '#338edc' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2b7bc4'}
                  onMouseLeave={e => e.currentTarget.style.background = '#338edc'}
                >
                  {t('claims.form.success.openClaim')}
                </button>
              )}
              <button
                onClick={() => navigate(isIntern ? '/claims' : '/portal')}
                className="w-full py-3 px-6 rounded-xl text-sm font-semibold"
                style={isIntern && success.claim_id
                  ? { background: 'transparent', color: '#338edc', border: '1px solid #338edc' }
                  : { background: '#338edc', color: '#fff' }}
              >
                {isIntern ? t('claims.form.success.backToList') : t('claims.form.success.backToPortal')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Step Renderers ──

  const carrierName = config.carriers.find(c => String(c.id) === String(form.carrier_id))?.name || '';

  // #1217 A2: Carrier-Liste nach Lager-Region filtern.
  // Fallback: Carrier ohne service_regions (leer/null) immer anzeigen.
  const filteredCarriers = (config.carriers || []).filter(c => {
    if (!form.lager) return true;
    const regions = Array.isArray(c.service_regions) ? c.service_regions : [];
    if (regions.length === 0) return true; // Fallback
    return regions.includes(form.lager);
  });

  // #1239 — Klärungsanfrage-Modus (Default): 1-Step-Form mit Minimal-Feldern.
  // Carrier/Lager/Claim-Type werden bei Eskalation aus dem Detail-View nachgepflegt.
  if (isClarificationMode) {
    return (
      <div className="min-h-screen p-4 md:p-8" style={{ background: 'var(--bg-page)' }}>
        <div className="max-w-xl mx-auto">
          <h1 className="text-xl font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
            {t('claims.form.title')}
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--text-tertiary)' }}>
            {t('claims.form.clarificationSubtitle')}
          </p>

          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 16, padding: 24 }}>
            <div className="space-y-5">
              {isIntern && (
                <FormField label={t('claims.form.selectCustomer')} hint={t('claims.form.customerHint')} error={errors.customer_id}>
                  <CustomerAutocompleteSearch
                    value={form.customer_id}
                    onChange={(id) => setField('customer_id', id)}
                    placeholder={t('claims.form.customerPlaceholder') || '— Kunde wählen —'}
                  />
                </FormField>
              )}

              <FormField label={t('claims.form.trackingNumber')} hint={t('claims.form.clarificationTrackingHint')} error={errors.tracking_number}>
                <input
                  type="text"
                  value={form.tracking_number}
                  onChange={e => setField('tracking_number', e.target.value)}
                  className="form-input"
                />
              </FormField>

              <FormField label={t('claims.form.orderNumberOptional')} hint={t('claims.form.orderNumberOptionalHint')}>
                <input
                  type="text"
                  value={form.order_number}
                  onChange={e => setField('order_number', e.target.value)}
                  className="form-input"
                  placeholder="ORD-12345"
                />
              </FormField>

              <FormField label={t('claims.form.clarificationDescription')} hint={t('claims.form.clarificationDescriptionHint')} error={errors.description}>
                <textarea
                  value={form.description}
                  onChange={e => setField('description', e.target.value)}
                  className="form-input"
                  rows={5}
                  style={{ resize: 'vertical' }}
                  placeholder={t('claims.form.clarificationDescriptionPlaceholder')}
                />
                <div className="text-xs mt-1" style={{ color: form.description.trim().length >= 10 ? 'var(--text-tertiary)' : '#EF4444' }}>
                  {form.description.trim().length}/10
                </div>
              </FormField>

              {errors.submit && (
                <div className="text-sm px-3 py-2 rounded-lg" style={{ background: '#FEF2F2', color: '#EF4444' }}>
                  {errors.submit}
                </div>
              )}
            </div>

            <div className="flex justify-between mt-8 pt-5 gap-3" style={{ borderTop: '1px solid var(--border-card)' }}>
              <button
                onClick={() => navigate(isPortal ? '/portal' : '/claims')}
                className="px-5 py-2.5 rounded-xl text-sm font-medium"
                style={{ color: 'var(--text-primary)', border: '1px solid var(--border-card)', background: 'transparent' }}
              >
                {t('claims.form.cancel')}
              </button>
              <button
                onClick={handleClarificationSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50"
                style={{ background: '#338edc' }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#2b7bc4'; }}
                onMouseLeave={e => e.currentTarget.style.background = '#338edc'}
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Check size={16} />
                )}
                {t('claims.form.clarificationCreate')}
              </button>
            </div>
          </div>
        </div>

        <style>{`
          .form-input {
            width: 100%;
            padding: 10px 14px;
            border-radius: 10px;
            border: 1px solid var(--border-input, #E5E7EB);
            background: var(--bg-input, #fff);
            color: var(--text-primary, #1A1A2E);
            font-size: 14px;
            outline: none;
            transition: border-color 0.15s;
          }
          .form-input:focus {
            border-color: #338edc;
            box-shadow: 0 0 0 2px rgba(51, 142, 220, 0.1);
          }
          .form-input::placeholder {
            color: var(--text-tertiary, #9CA3AF);
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'var(--bg-page)' }}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <h1 className="text-xl font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
          {t('claims.form.title')}
        </h1>
        <p className="text-sm mb-6" style={{ color: 'var(--text-tertiary)' }}>
          {t('claims.form.subtitle')}
        </p>

        {/* Progress Bar */}
        <div className="flex items-center mb-8 gap-0">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className="flex items-center gap-2 flex-shrink-0">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors"
                  style={{
                    background: i < step ? '#10B981' : i === step ? '#338edc' : 'var(--bg-inset)',
                    color: i <= step ? '#fff' : 'var(--text-tertiary)',
                    border: i > step ? '1px solid var(--border-card)' : 'none',
                  }}
                >
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                <span className="text-xs font-medium hidden sm:block" style={{ color: i === step ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                  {t(label)}
                </span>
              </div>
              {i < 3 && <div className="flex-1 h-px mx-2" style={{ background: i < step ? '#10B981' : 'var(--border-card)' }} />}
            </div>
          ))}
        </div>

        {/* Card */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 16, padding: 24 }}>

          {/* Step 1: Grunddaten */}
          {step === 0 && (
            <div className="space-y-5">
              {/* #1221 Bug 7a: Kunden-Auswahl Pflicht für Intern-User */}
              {isIntern && (
                <FormField label={t('claims.form.selectCustomer')} hint={t('claims.form.customerHint')} error={errors.customer_id}>
                  <CustomerAutocompleteSearch
                    value={form.customer_id}
                    onChange={(id) => setField('customer_id', id)}
                    placeholder={t('claims.form.customerPlaceholder') || '— Kunde wählen —'}
                  />
                </FormField>
              )}
              {/* #1217 A2: Lager-Auswahl ZUERST (filtert Carrier dynamisch) */}
              <FormField label={t('claims.form.selectLager')} hint={t('claims.form.lagerHint')} error={errors.lager}>
                <div className="flex gap-3">
                  <RadioCard
                    selected={form.lager === 'AT'}
                    onClick={() => {
                      setField('lager', 'AT');
                      // Carrier-Auswahl zurücksetzen, falls aktueller Carrier nicht zur neuen Region passt
                      if (form.carrier_id) {
                        const c = config.carriers.find(cc => String(cc.id) === String(form.carrier_id));
                        const regions = Array.isArray(c?.service_regions) ? c.service_regions : [];
                        if (regions.length > 0 && !regions.includes('AT')) setField('carrier_id', '');
                      }
                    }}
                    label={t('claims.form.lagerAT')}
                  />
                  <RadioCard
                    selected={form.lager === 'DE'}
                    onClick={() => {
                      setField('lager', 'DE');
                      if (form.carrier_id) {
                        const c = config.carriers.find(cc => String(cc.id) === String(form.carrier_id));
                        const regions = Array.isArray(c?.service_regions) ? c.service_regions : [];
                        if (regions.length > 0 && !regions.includes('DE')) setField('carrier_id', '');
                      }
                    }}
                    label={t('claims.form.lagerDE')}
                  />
                </div>
              </FormField>

              <FormField label={t('claims.form.orderNumber')} hint={t('claims.form.orderNumberHint')} error={errors.order_number}>
                <input
                  type="text"
                  value={form.order_number}
                  onChange={e => setField('order_number', e.target.value)}
                  className="form-input"
                  placeholder="ORD-12345"
                />
              </FormField>

              <FormField label={t('claims.form.selectCarrier')} error={errors.carrier_id}>
                <select
                  value={form.carrier_id}
                  onChange={e => setField('carrier_id', e.target.value)}
                  className="form-input"
                  disabled={!form.lager}
                >
                  <option value="">{t('claims.form.selectCarrier')}</option>
                  {filteredCarriers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </FormField>

              <FormField label={t('claims.form.trackingNumber')} error={errors.tracking_number}>
                <input
                  type="text"
                  value={form.tracking_number}
                  onChange={e => setField('tracking_number', e.target.value)}
                  className="form-input"
                />
              </FormField>

              <FormField label={t('claims.form.claimType')} error={errors.claim_type}>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <RadioCard
                    selected={form.claim_type === 'damage'}
                    onClick={() => setField('claim_type', 'damage')}
                    label={t('claims.form.typeDamage')}
                  />
                  <RadioCard
                    selected={form.claim_type === 'investigation'}
                    onClick={() => setField('claim_type', 'investigation')}
                    label={t('claims.form.typeInvestigation')}
                  />
                  {/* #1217 A1 */}
                  <RadioCard
                    selected={form.claim_type === 'content_missing'}
                    onClick={() => setField('claim_type', 'content_missing')}
                    label={t('claims.form.typeContentMissing')}
                  />
                </div>
              </FormField>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 1 && (
            <div className="space-y-5">
              {form.claim_type === 'damage' && (
                <>
                  <FormField label={t('claims.form.damageDescription')} hint={t('claims.form.damageDescriptionHint')} error={errors.description}>
                    <textarea
                      value={form.description}
                      onChange={e => setField('description', e.target.value)}
                      className="form-input"
                      rows={4}
                      style={{ resize: 'vertical' }}
                    />
                    <div className="text-xs mt-1" style={{ color: form.description.length >= 20 ? 'var(--text-tertiary)' : '#EF4444' }}>
                      {form.description.length}/20
                    </div>
                  </FormField>

                  <FormField label={t('claims.form.damagePhotos')} hint={t('claims.form.damagePhotosHint')} error={errors.damagePhotos}>
                    <DropZone
                      files={damagePhotos}
                      onDrop={e => handleFiles(e, setDamagePhotos, damagePhotos, 10, true)}
                      onRemove={i => removeFile(setDamagePhotos, damagePhotos, i)}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      t={t}
                      showThumbnails
                    />
                  </FormField>

                  {/* #1216 A3: Verpackungsfoto Pflicht bei damage */}
                  <FormField label={t('claims.form.packagingPhotosRequired')} hint={t('claims.form.packagingPhotosHint')} error={errors.packagingPhotos}>
                    <DropZone
                      files={packagingPhotos}
                      onDrop={e => handleFiles(e, setPackagingPhotos, packagingPhotos, 10, true)}
                      onRemove={i => removeFile(setPackagingPhotos, packagingPhotos, i)}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      t={t}
                      showThumbnails
                    />
                  </FormField>
                </>
              )}

              {/* #1217 A1: content_missing — Beschreibung was fehlt + Verpackungsfoto Pflicht */}
              {form.claim_type === 'content_missing' && (
                <>
                  <FormField label={t('claims.form.contentMissingDescription')} hint={t('claims.form.contentMissingDescriptionHint')} error={errors.description}>
                    <textarea
                      value={form.description}
                      onChange={e => setField('description', e.target.value)}
                      className="form-input"
                      rows={4}
                      style={{ resize: 'vertical' }}
                    />
                    <div className="text-xs mt-1" style={{ color: form.description.length >= 20 ? 'var(--text-tertiary)' : '#EF4444' }}>
                      {form.description.length}/20
                    </div>
                  </FormField>

                  <FormField label={t('claims.form.packagingPhotosRequired')} hint={t('claims.form.packagingPhotosHint')} error={errors.packagingPhotos}>
                    <DropZone
                      files={packagingPhotos}
                      onDrop={e => handleFiles(e, setPackagingPhotos, packagingPhotos, 10, true)}
                      onRemove={i => removeFile(setPackagingPhotos, packagingPhotos, i)}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      t={t}
                      showThumbnails
                    />
                  </FormField>
                </>
              )}

              {form.claim_type === 'investigation' && (
                <>
                  <FormField label={t('claims.form.lastKnownStatus')}>
                    <input
                      type="text"
                      value={form.last_known_status}
                      onChange={e => setField('last_known_status', e.target.value)}
                      className="form-input"
                    />
                  </FormField>

                  {/* #1216 B2: investigation → "Notiz zum Fallverlauf" statt "Empfängerdaten" */}
                  <FormField label={t('claims.form.notesCaseProgress')} hint={t('claims.form.notesCaseProgressHint')}>
                    <textarea
                      value={form.recipient_info}
                      onChange={e => setField('recipient_info', e.target.value)}
                      className="form-input"
                      rows={3}
                      style={{ resize: 'vertical' }}
                      placeholder={t('claims.form.notesCaseProgressPlaceholder')}
                    />
                  </FormField>
                </>
              )}
            </div>
          )}

          {/* Step 3: Dokumente */}
          {step === 2 && (
            <div className="space-y-5">
              <FormField label={t('claims.form.goodsValueProof')} hint={t('claims.form.goodsValueProofHint')} error={errors.goodsValueProof}>
                {config.template_url && (
                  <a
                    href={config.template_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium mb-3 inline-block"
                    style={{ color: '#338edc' }}
                  >
                    {t('claims.form.downloadTemplate')}
                  </a>
                )}
                <DropZone
                  files={goodsValueProof}
                  onDrop={e => handleFiles(e, setGoodsValueProof, goodsValueProof, 1, false)}
                  onRemove={i => removeFile(setGoodsValueProof, goodsValueProof, i)}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  t={t}
                />
              </FormField>

              <FormField label={t('claims.form.salesInvoice')} hint={t('claims.form.salesInvoiceHint')} error={errors.salesInvoice}>
                <DropZone
                  files={salesInvoice}
                  onDrop={e => handleFiles(e, setSalesInvoice, salesInvoice, 1, false)}
                  onRemove={i => removeFile(setSalesInvoice, salesInvoice, i)}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  t={t}
                />
              </FormField>

              {/* #1216 D3: Sonstige Dokumente (optional, multiple) */}
              <FormField label={t('claims.form.otherDocuments')} hint={t('claims.form.otherDocumentsHint')}>
                <DropZone
                  files={otherDocuments}
                  onDrop={e => handleFiles(e, setOtherDocuments, otherDocuments, 10, false)}
                  onRemove={i => removeFile(setOtherDocuments, otherDocuments, i)}
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  t={t}
                />
              </FormField>
            </div>
          )}

          {/* Step 4: Bestätigung */}
          {step === 3 && (
            <div className="space-y-5">
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('claims.form.summary')}</h3>

              <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--bg-inset)', border: '1px solid var(--border-card)' }}>
                {/* #1217 A2: Lager in Summary */}
                {form.lager && (
                  <SummaryRow
                    label={t('claims.fields.lager')}
                    value={form.lager === 'AT' ? t('claims.form.lagerAT') : t('claims.form.lagerDE')}
                  />
                )}
                <SummaryRow label={t('claims.form.orderNumber')} value={form.order_number} />
                <SummaryRow label={t('claims.form.selectCarrier')} value={carrierName} />
                <SummaryRow label={t('claims.form.trackingNumber')} value={form.tracking_number} />
                <SummaryRow
                  label={t('claims.form.claimType')}
                  value={
                    form.claim_type === 'damage' ? t('claims.form.typeDamage')
                    : form.claim_type === 'content_missing' ? t('claims.form.typeContentMissing')
                    : t('claims.form.typeInvestigation')
                  }
                />
                {(form.claim_type === 'damage' || form.claim_type === 'content_missing') && form.description && (
                  <SummaryRow
                    label={form.claim_type === 'damage' ? t('claims.form.damageDescription') : t('claims.form.contentMissingDescription')}
                    value={form.description}
                  />
                )}
                {form.claim_type === 'investigation' && form.last_known_status && (
                  <SummaryRow label={t('claims.form.lastKnownStatus')} value={form.last_known_status} />
                )}
              </div>

              {/* #1217 D1: Bearbeitungs-Hinweise (optional, intern) */}
              <div>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>{t('claims.form.processingHints')}</h3>
                <div className="space-y-4">
                  <FormField label={t('claims.form.submissionChannel')} hint={t('claims.form.submissionChannelHint')}>
                    <select
                      value={form.submission_channel}
                      onChange={e => setField('submission_channel', e.target.value)}
                      className="form-input"
                    >
                      <option value="">—</option>
                      <option value="business_cockpit">{t('claims.form.submissionChannelBusinessCockpit')}</option>
                      <option value="email">{t('claims.form.submissionChannelEmail')}</option>
                      <option value="phone">{t('claims.form.submissionChannelPhone')}</option>
                      <option value="portal">{t('claims.form.submissionChannelPortal')}</option>
                      <option value="manual">{t('claims.form.submissionChannelManual')}</option>
                    </select>
                  </FormField>

                  {/* Post-Tracking-Nr nur sinnvoll wenn Carrier "Post" enthält */}
                  {carrierName.toLowerCase().includes('post') && (
                    <FormField label={t('claims.form.postTrackingNr')} hint={t('claims.form.postTrackingNrHint')}>
                      <input
                        type="text"
                        value={form.post_tracking_nr}
                        onChange={e => setField('post_tracking_nr', e.target.value)}
                        className="form-input"
                      />
                    </FormField>
                  )}

                  <FormField label={t('claims.form.carrierTicketNr')} hint={t('claims.form.carrierTicketNrHint')}>
                    <input
                      type="text"
                      value={form.carrier_ticket_nr}
                      onChange={e => setField('carrier_ticket_nr', e.target.value)}
                      className="form-input"
                    />
                  </FormField>
                </div>
              </div>

              {/* Thumbnails */}
              {damagePhotos.length > 0 && (
                <div>
                  <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.damagePhotos')}</div>
                  <div className="flex gap-2 flex-wrap">
                    {damagePhotos.map((f, i) => <Thumb key={i} file={f} />)}
                  </div>
                </div>
              )}

              {/* Document list */}
              <div>
                <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.step3')}</div>
                <div className="space-y-1">
                  {goodsValueProof.map((f, i) => (
                    <div key={`gv-${i}`} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <FileText size={14} style={{ color: 'var(--text-tertiary)' }} /> {f.name}
                    </div>
                  ))}
                  {salesInvoice.map((f, i) => (
                    <div key={`si-${i}`} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <FileText size={14} style={{ color: 'var(--text-tertiary)' }} /> {f.name}
                    </div>
                  ))}
                  {/* #1216 D3: Sonstige Dokumente im Summary */}
                  {otherDocuments.map((f, i) => (
                    <div key={`od-${i}`} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
                      <FileText size={14} style={{ color: 'var(--text-tertiary)' }} /> {f.name}
                    </div>
                  ))}
                </div>
              </div>

              {/* Confirm checkbox */}
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.confirmed}
                  onChange={e => setField('confirmed', e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded accent-[#338edc]"
                />
                <span className="text-sm" style={{ color: errors.confirmed ? '#EF4444' : 'var(--text-primary)' }}>
                  {t('claims.form.confirm')}
                </span>
              </label>

              {errors.submit && (
                <div className="text-sm px-3 py-2 rounded-lg" style={{ background: '#FEF2F2', color: '#EF4444' }}>
                  {errors.submit}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-5" style={{ borderTop: '1px solid var(--border-card)' }}>
            <div>
              {step > 0 && (
                <button
                  onClick={goBack}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
                  style={{ color: 'var(--text-primary)', border: '1px solid var(--border-card)', background: 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <ChevronLeft size={16} />
                  {t('claims.form.back')}
                </button>
              )}
            </div>
            <div>
              {step < 3 ? (
                <button
                  onClick={goNext}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors"
                  style={{ background: '#338edc' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2b7bc4'}
                  onMouseLeave={e => e.currentTarget.style.background = '#338edc'}
                >
                  {t('claims.form.next')}
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-colors disabled:opacity-50"
                  style={{ background: '#338edc' }}
                  onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#2b7bc4'; }}
                  onMouseLeave={e => e.currentTarget.style.background = '#338edc'}
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  {t('claims.form.submit')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inline styles for form inputs */}
      <style>{`
        .form-input {
          width: 100%;
          padding: 10px 14px;
          border-radius: 10px;
          border: 1px solid var(--border-input, #E5E7EB);
          background: var(--bg-input, #fff);
          color: var(--text-primary, #1A1A2E);
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s;
        }
        .form-input:focus {
          border-color: #338edc;
          box-shadow: 0 0 0 2px rgba(51, 142, 220, 0.1);
        }
        .form-input::placeholder {
          color: var(--text-tertiary, #9CA3AF);
        }
      `}</style>
    </div>
  );
}

// ── Sub-components ──

function FormField({ label, hint, error, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: error ? '#EF4444' : 'var(--text-secondary)' }}>
        {label}
      </label>
      {hint && <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      {children}
    </div>
  );
}

function RadioCard({ selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex-1 text-left px-4 py-3 rounded-xl text-sm font-medium transition-all"
      style={{
        border: selected ? '2px solid #338edc' : '1px solid var(--border-card)',
        background: selected ? '#EBF4FD' : 'var(--bg-card)',
        color: selected ? '#338edc' : 'var(--text-primary)',
      }}
    >
      {label}
    </button>
  );
}

function DropZone({ files, onDrop, onRemove, accept, t, showThumbnails }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div>
      <div
        className="relative rounded-xl p-6 text-center cursor-pointer transition-colors"
        style={{
          border: `2px dashed ${dragOver ? '#338edc' : 'var(--border-card)'}`,
          background: dragOver ? '#EBF4FD' : 'transparent',
        }}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { handleDrag(e); setDragOver(true); }}
        onDragEnter={e => { handleDrag(e); setDragOver(true); }}
        onDragLeave={e => { handleDrag(e); setDragOver(false); }}
        onDrop={e => { handleDrag(e); setDragOver(false); onDrop(e); }}
      >
        <Upload size={24} style={{ color: 'var(--text-tertiary)', margin: '0 auto 8px' }} />
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('claims.form.dragDrop')}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={onDrop}
        />
      </div>

      {files.length > 0 && (
        <div className={`mt-3 ${showThumbnails ? 'flex gap-2 flex-wrap' : 'space-y-1'}`}>
          {files.map((f, i) => (
            showThumbnails ? (
              <Thumb key={i} file={f} onRemove={() => onRemove(i)} />
            ) : (
              <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg" style={{ background: 'var(--bg-inset)', border: '1px solid var(--border-card)' }}>
                <div className="flex items-center gap-2 text-sm min-w-0" style={{ color: 'var(--text-primary)' }}>
                  <FileText size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  <span className="truncate">{f.name}</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); onRemove(i); }} className="flex-shrink-0 p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10">
                  <X size={14} style={{ color: '#EF4444' }} />
                </button>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}

function Thumb({ file, onRemove }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  return (
    <div className="relative w-20 h-20 rounded-lg overflow-hidden group" style={{ border: '1px solid var(--border-card)' }}>
      <img src={url} alt={file.name} className="w-full h-full object-cover" />
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X size={10} />
        </button>
      )}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span className="text-sm font-medium text-right" style={{ color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
